import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  AtsCreateQuestionTemplateDocument,
  AtsJobPostingSettingsCustomQuestionFragment,
  AtsQuestionOptionInput,
  AtsUpdateJobPostingSettingsCustomQuestionsDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EditQuestion } from 'modules/ats/components/Questions/EditQuestion'
import { CreateQuestionFormValues } from 'modules/ats/components/Questions/QuestionFormValues'
import { questionTypeHasOptions } from 'modules/ats/lib/utils/customQuestion'
import { validateQuestions } from 'modules/ats/lib/utils/questions_validations'

interface FormValues {
  label: string
  mandatory: boolean
  autoDisqualify: boolean
  options: AtsQuestionOptionInput[]
}

interface Props {
  customQuestion: AtsJobPostingSettingsCustomQuestionFragment
  canSaveTemplates: boolean
  companyId: number
  onClose: () => void
}

export const EditCustomQuestion = ({
  onClose,
  customQuestion,
  canSaveTemplates,
  companyId,
}: Props) => {
  const [update, { loading }] = useMutation(AtsUpdateJobPostingSettingsCustomQuestionsDocument, {
    extractorFn: (data) => data.atsMutations.updateAtsQuestion,
  })

  const [createQuestionTemplate] = useMutation(AtsCreateQuestionTemplateDocument, {
    extractorFn: (data) => data.atsMutations.createAtsQuestionTemplate,
  })

  const [saveAsTemplate, setSaveAsTemplate] = useState(false)
  const form = useForm<FormValues>({
    entity: customQuestion,
    defaultValues: {
      label: '',
      mandatory: false,
      autoDisqualify: false,
      options: customQuestion.options || [],
    },
    validate(data) {
      validateQuestions(data, {})
    },
    async onSubmit(values, { setErrors }) {
      const shouldSendOptions = questionTypeHasOptions(customQuestion.questionType)

      const result = await update({
        variables: {
          id: customQuestion.id,
          label: values.label,
          mandatory: values.mandatory,
          autoDisqualify: values.autoDisqualify,
          options: shouldSendOptions
            ? form.values.options?.map((option) => ({
                text: option.text,
                disqualifies: option.disqualifies,
              }))
            : null,
          position: customQuestion.position,
        },
      })

      const handled = handleResult(result, ({ label, mandatory }) => {
        setErrors({
          label,
          mandatory,
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      if (saveAsTemplate) {
        await submitTemplateForm({
          ...values,
          questionType: customQuestion.questionType,
        })
      }

      onClose()

      return handled
    },
  })

  const templateForm = useForm<CreateQuestionFormValues>({
    defaultValues: {
      label: '',
      mandatory: false,
      autoDisqualify: false,
      questionType: 'long_text',
      options: [],
    },
    validate(data) {
      return validateQuestions(data, {})
    },
    async onSubmit(values, { setErrors }) {
      const shouldSendOptions = questionTypeHasOptions(values.questionType)

      const result = await createQuestionTemplate({
        variables: {
          companyId: companyId,
          options: shouldSendOptions ? values.options : null,
          autoDisqualify: values.autoDisqualify,
          label: values.label,
          mandatory: values.mandatory,
          questionType: values.questionType,
        },
      })

      const handled = handleResult(result, ({ label, mandatory, questionType }) => {
        setErrors({
          label,
          mandatory,
          questionType,
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return handled
    },
  })

  const submitTemplateForm = async (values: CreateQuestionFormValues) => {
    templateForm.setValues({
      label: values.label,
      mandatory: values.mandatory,
      autoDisqualify: values.autoDisqualify,
      questionType: values.questionType,
      options: values.options,
    })

    await createQuestionTemplate({
      variables: {
        companyId: companyId,
        options: questionTypeHasOptions(values.questionType) ? values.options : null,
        autoDisqualify: values.autoDisqualify,
        label: values.label,
        mandatory: values.mandatory,
        questionType: values.questionType,
      },
    })
  }

  const hasOptions = questionTypeHasOptions(customQuestion.questionType)

  return (
    <Modal
      illustration="server-edit"
      title={i18n.t('ats.job_posting_settings.application_form.custom_questions.edit_modal_title')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Button loading={loading} submit>
          {i18n.t('common.save')}
        </Button>
      }
    >
      <EditQuestion
        form={form}
        label={customQuestion.label}
        questionType={customQuestion.questionType}
        hasOptions={hasOptions}
        mandatory={customQuestion.mandatory}
        canSaveTemplates={canSaveTemplates}
        saveAsTemplate={saveAsTemplate}
        setSaveAsTemplate={setSaveAsTemplate}
      />
    </Modal>
  )
}
